import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import type { ScoreTrendPoint } from '../../types/admin';

interface Props {
  data: ScoreTrendPoint[];
}

export default function ScoreTrendChart({ data }: Props) {
  const { t } = useTranslation();
  if (data.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        {t('dashboard.scoreTrend.empty')}
      </div>
    );
  }

  const locale = t('dashboard.scoreTrend.dateLocale');
  const formatted = data.map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString(locale, { month: 'short', day: 'numeric' }),
  }));

  return (
    <div role="img" aria-label={t('dashboard.scoreTrend.ariaLabel')}>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={formatted} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-white/10" opacity={0.5} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} className="text-slate-500 dark:text-slate-400" />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} className="text-slate-500 dark:text-slate-400" />
          <Tooltip
            contentStyle={{ backgroundColor: '#141C2B', border: '1px solid rgba(255,255,255,0.13)', borderRadius: '12px', fontSize: 12, color: '#EEF2F8' }}
            labelStyle={{ color: '#94A3B8' }}
            itemStyle={{ color: '#2563EB' }}
            formatter={(value) => [`${value}%`, t('dashboard.scoreTrend.tooltipScore')]}
          />
          <ReferenceLine y={70} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: t('dashboard.scoreTrend.passLabel'), fill: '#D97706', fontSize: 10, position: 'right' }} />
          <Line
            type="monotone"
            dataKey="score_percent"
            stroke="#2563EB"
            strokeWidth={2}
            dot={{ r: 4, fill: '#2563EB' }}
            activeDot={{ r: 6, fill: '#60A5FA' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
